import { create } from 'zustand';
import { shallow } from 'zustand/shallow';

export type BasicRegistrationInfo = {
  email: string;
  password: string;
  birthDate: {
    day: string;
    month: string;
    year: string;
  };
  mobile: {
    number: string;
    country: string;
    prefix: string;
    wholeNumber: string;
  };
  fitToPlayConsent: boolean;
};

export type AddressRegistrationInfo = {
  firstName: string;
  lastName: string;
  address: string;
  houseNumber: string;
  postCode: string;
  city: string;
  provinceCode: string;
};

export type WorkRegistrationInfo = {
  industryId: string;
  occupationId: string;
  citizenshipId: string;
};

type State = BasicRegistrationInfo & AddressRegistrationInfo & WorkRegistrationInfo;

type Actions = {
  updateFields: (fields: Partial<State>) => void;
  reset: () => void;
};

const initialState: State = {
  email: '',
  password: '',
  birthDate: { day: '', month: '', year: '' },
  mobile: {
    number: '',
    country: 'ca',
    prefix: '',
    wholeNumber: '',
  },
  fitToPlayConsent: false,
  firstName: '',
  lastName: '',
  address: '',
  houseNumber: '',
  postCode: '',
  city: '',
  provinceCode: '',
  industryId: '',
  occupationId: '',
  citizenshipId: 'ca',
};

export const useRegistrationStore = create<{ data: State } & Actions>(set => ({
  data: initialState,
  updateFields: (fields: Partial<State>) => {
    set(state => ({ data: { ...state.data, ...fields } }));
  },
  reset: () => set({ data: initialState }),
}));

export const useRegistrationData = () => {
  return useRegistrationStore(state => state.data, shallow);
};

export const useBasicInfoData = () => {
  return useRegistrationStore(
    state => ({
      email: state.data.email,
      password: state.data.password,
      birthDate: state.data.birthDate,
      mobile: state.data.mobile,
      fitToPlayConsent: state.data.fitToPlayConsent,
    }),
    shallow,
  );
};

export const useAddressInfoData = () => {
  return useRegistrationStore(
    state => ({
      firstName: state.data.firstName,
      lastName: state.data.lastName,
      address: state.data.address,
      houseNumber: state.data.houseNumber,
      postCode: state.data.postCode,
      city: state.data.city,
      provinceCode: state.data.provinceCode,
    }),
    shallow,
  );
};

export const useWorkInfoData = () => {
  return useRegistrationStore(
    state => ({
      citizenshipId: state.data.citizenshipId,
      industryId: state.data.industryId,
      occupationId: state.data.occupationId,
    }),
    shallow,
  );
};

export const useRegistrationActions = () => {
  return useRegistrationStore(
    state => ({
      updateFields: state.updateFields,
      reset: state.reset,
    }),
    shallow,
  );
};
